// ksp-calculator.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Konstanta Hasil Kali Kelarutan (Ksp) - Hitung Kelarutan dan Konsentrasi Molar";
const DESCRIPTION =
  "Kalkulator Konstanta Hasil Kali Kelarutan (Ksp) gratis - hitung nilai Ksp atau kelarutan molar senyawa ionik secara akurat. Alat pendidikan kimia yang mendukung berbagai jenis garam dengan penjelasan rumus dan contoh.";
const KEYWORDS =
  "kalkulator Ksp, konstanta hasil kali kelarutan, hitung kelarutan molar, kimia analitik, kesetimbangan ion, solubility product constant calculator, molar solubility";
const OG_URL = "https://do-calculate.com/calculator/id/ksp-calculator/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Konstanta Hasil Kali Kelarutan (Ksp)",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "EducationApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator Sains",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Ksp",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Apa itu Konstanta Hasil Kali Kelarutan (Ksp)?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Konstanta hasil kali kelarutan (Ksp) adalah konstanta kesetimbangan untuk senyawa ionik yang sukar larut dalam air, dan merupakan hasil kali konsentrasi ion-ion pembentuk garam yang masing-masing dipangkatkan dengan koefisiennya dalam persamaan kesetimbangan.",
        },
      },
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung Ksp dari kelarutan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tergantung pada jenis garam. Untuk garam AB, Ksp = s². Untuk garam AB₂, Ksp = 4s³. Untuk garam A₂B₃, Ksp = 108s⁵, di mana s adalah kelarutan molar.",
        },
      },
    ],
  },
];

const SALT_TYPES = {
  AB: { label: "Garam Biner (AB) seperti AgCl", x: 1, y: 1 },
  AB2: { label: "Garam Terner (AB₂) seperti Mg(OH)₂", x: 1, y: 2 },
  A2B: { label: "Garam Terner (A₂B) seperti Ag₂CrO₄", x: 2, y: 1 },
  AB3: { label: "Garam Kuartener (AB₃) seperti Al(OH)₃", x: 1, y: 3 },
  A3B: { label: "Garam Kuartener (A₃B) seperti Li₃PO₄", x: 3, y: 1 },
  A2B3: { label: "Garam Kuintener (A₂B₃) seperti As₂S₃", x: 2, y: 3 },
  A3B2: { label: "Garam Kuintener (A₃B₂) seperti Ca₃(PO₄)₂", x: 3, y: 2 },
};

function KspCalculator() {
  const [calcMode, setCalcMode] = useState("s_to_ksp");
  const [saltType, setSaltType] = useState("AB");
  const [molarSolubility, setMolarSolubility] = useState("");
  const [kspValue, setKspValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateKsp = () => {
    try {
      setError("");
      const type = SALT_TYPES[saltType];
      const x = type.x;
      const y = type.y;

      if (calcMode === "s_to_ksp") {
        const s = parseFloat(molarSolubility);
        if (isNaN(s) || s <= 0) {
          setError("Harap masukkan nilai kelarutan molar yang valid (lebih besar dari nol)");
          setResult(null);
          return;
        }

        const ksp = Math.pow(x, x) * Math.pow(y, y) * Math.pow(s, x + y);
        setResult({
          ksp: ksp,
          s: s,
          formula: `Ksp = [A]${x > 1 ? `^${x}` : ""} [B]${y > 1 ? `^${y}` : ""}`,
          mathExpr: `(${x}^${x}) * (${y}^${y}) * s^${x + y}`,
        });
      } else {
        const ksp = parseFloat(kspValue);
        if (isNaN(ksp) || ksp <= 0) {
          setError("Harap masukkan nilai Ksp yang valid (lebih besar dari nol)");
          setResult(null);
          return;
        }

        const divisor = Math.pow(x, x) * Math.pow(y, y);
        const s = Math.pow(ksp / divisor, 1 / (x + y));
        setResult({
          ksp: ksp,
          s: s,
          formula: `s = (Ksp / (${x}^${x} * ${y}^${y}))^(1/${x + y})`,
          mathExpr: `s = [Ksp / ${divisor}]^(1/${x + y})`,
        });
      }
    } catch (err) {
      setError("Terjadi kesalahan dalam perhitungan. Harap periksa input.");
      setResult(null);
    }
  };

  useEffect(() => {
    if (calcMode === "s_to_ksp" && molarSolubility) {
      calculateKsp();
    } else if (calcMode === "ksp_to_s" && kspValue) {
      calculateKsp();
    }
  }, [calcMode, saltType, molarSolubility, kspValue]);

  const handleClear = () => {
    setMolarSolubility("");
    setKspValue("");
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Konstanta Hasil Kali Kelarutan (Ksp)</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                Kalkulator ini membantu Anda <strong>menghitung konstanta hasil kali kelarutan (Ksp)</strong> dari kelarutan molar, atau sebaliknya. Konstanta hasil kali kelarutan adalah ukuran seberapa larut senyawa ionik yang sukar larut dalam larutan air, dan sangat penting untuk memahami proses pengendapan dan kesetimbangan kimia.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Pengaturan Perhitungan</h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label >Apa yang ingin Anda hitung?</label>
                  <select
                    className="form-select"
                    value={calcMode}
                    onChange={(e) => {
                      setCalcMode(e.target.value);
                      setResult(null);
                    }}
                  >
                    <option value="s_to_ksp">Hitung Ksp dari kelarutan (s)</option>
                    <option value="ksp_to_s">Hitung kelarutan (s) dari Ksp</option>
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >Jenis Garam / Rumus</label>
                  <select
                    className="form-select"
                    value={saltType}
                    onChange={(e) => setSaltType(e.target.value)}
                  >
                    {Object.entries(SALT_TYPES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                {calcMode === "s_to_ksp" ? (
                  <div className="col-sm-12">
                    <label >Kelarutan Molar (s) [mol/L]</label>
                    <input
                      type="number"
                      className="form-control"
                      value={molarSolubility}
                      onChange={(e) => setMolarSolubility(e.target.value)}
                      placeholder="Contoh: 0,0013"
                    />
                  </div>
                ) : (
                  <div className="col-sm-12">
                    <label >Nilai Konstanta Hasil Kali Kelarutan (Ksp)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={kspValue}
                      onChange={(e) => setKspValue(e.target.value)}
                      placeholder="Contoh: 1,8e-10"
                    />
                  </div>
                )}
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  Hapus Data
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Hasil:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Variabel</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nilai</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Konstanta Hasil Kali Kelarutan (Ksp)</td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.ksp.toExponential(4)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Kelarutan Molar (s)</td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#0f3d28" }}>
                            {result.s.toExponential(4)} mol/L
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header"><strong>Logika Perhitungan</strong></div>
                      <div className="info-section-body">
                        <p><strong>Persamaan yang Digunakan:</strong> {result.formula}</p>
                        <p><strong>Hubungan Matematis:</strong> {result.mathExpr}</p>
                        <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                          Nilai dihitung berdasarkan koefisien ion (Stoikiometri) dari garam yang dipilih.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>Apa itu Konstanta Hasil Kali Kelarutan (Ksp)?</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Konstanta hasil kali kelarutan (Ksp) adalah jenis konstanta kesetimbangan yang berlaku untuk garam ionik yang sukar larut. Ketika garam padat ditempatkan dalam air, terjadi kesetimbangan antara garam padat dan ion-ion terlarut dalam larutan jenuh.
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Larutan Jenuh:</strong> adalah larutan yang mengandung jumlah maksimum zat terlarut pada suhu tertentu.</li>
                  <li><strong>Kepentingan:</strong> Ksp digunakan untuk memprediksi apakah endapan akan terbentuk ketika dua larutan ionik dicampur.</li>
                  <li><strong>Hubungan dengan Kelarutan:</strong> Semakin besar nilai Ksp, semakin besar kelarutan garam dalam air.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Tabel Hubungan Ksp dan Kelarutan (s)</strong></div>
              <div className="info-section-body">
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th >Jenis Garam</th>
                      <th >Contoh</th>
                      <th >Hubungan Ksp dengan (s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>AB</td><td>AgCl</td><td>Ksp = s²</td></tr>
                    <tr>.<td>AB₂ atau A₂B</td><td>PbCl₂, Ag₂CrO₄</td><td>Ksp = 4s³</td></tr>
                    <tr><td>AB₃ atau A₃B</td><td>Al(OH)₃</td><td>Ksp = 27s⁴</td></tr>
                    <tr><td>A₂B₃</td><td>As₂S₃</td><td>Ksp = 108s⁵</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Contoh Praktis</strong></div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px" }}>Contoh 1: Menghitung Ksp untuk Garam AgCl</h3>
                <p>Jika kelarutan perak klorida (AgCl) adalah 1,3 × 10⁻⁵ mol/L:</p>
                <p>Karena ini adalah garam tipe AB, maka Ksp = s² = (1,3 × 10⁻⁵)² = 1,69 × 10⁻¹⁰.</p>

                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Contoh 2: Menghitung Kelarutan dari Ksp</h3>
                <p>Jika Ksp untuk magnesium hidroksida Mg(OH)₂ adalah 1,8 × 10⁻¹¹:</p>
                <p>Karena ini adalah garam tipe AB₂, maka Ksp = 4s³, dan dengan menyelesaikan untuk s, kita dapatkan s = 1,65 × 10⁻⁴ mol/L.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Pertanyaan Umum (FAQ)</strong></div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>1. Apakah nilai Ksp berubah seiring perubahan suhu?</strong>
                  <p>Ya, konstanta hasil kali kelarutan adalah konstanta kesetimbangan, dan nilainya sepenuhnya bergantung pada suhu. Biasanya, kelarutan dan Ksp meningkat dengan meningkatnya suhu untuk sebagian besar garam.</p>
                </div>
                <div className="mb-3">
                  <strong>2. Apa perbedaan antara kelarutan dan konstanta hasil kali kelarutan?</strong>
                  <p>Kelarutan adalah jumlah zat yang larut membentuk larutan jenuh (diukur dalam g/L atau mol/L), sedangkan Ksp adalah hasil kali konsentrasi ion-ion pada kesetimbangan.</p>
                </div>
                <div className="mb-3">
                  <strong>3. Apa yang dimaksud dengan "efek ion senama"?</strong>
                  <p>Penambahan ion senama ke dalam larutan akan mengurangi kelarutan garam yang sukar larut menurut prinsip Le Chatelier, menyebabkan sebagian garam terlarut mengendap.</p>
                </div>
                <div className="mb-3">
                  <strong>4. Bagaimana saya tahu jika garam akan mengendap?</strong>
                  <p>Hasil kali konsentrasi aktual (Qsp) dihitung. Jika Qsp  Ksp, endapan akan terbentuk sampai larutan mencapai keadaan jenuh.</p>
                </div>
                <div className="mb-3">
                  <strong>5. Bisakah saya membandingkan kelarutan dua garam hanya dengan melihat Ksp?</strong>
                  <p>Ini hanya bisa dilakukan jika kedua garam memiliki jenis yang sama (misalnya, keduanya AB). Jika jenis garamnya berbeda, kelarutan molar harus dihitung untuk perbandingan yang akurat.</p>
                </div>
                <div className="mb-3">
                  <strong>6. Bagaimana cara mengkonversi dari g/L ke mol/L untuk menggunakan kalkulator?</strong>
                  <p>Bagi kelarutan dalam gram/liter dengan massa molar garam untuk mendapatkan kelarutan molar (s).</p>
                </div>
                <div className="mb-3">
                  <strong>7. Mengapa padatan tidak muncul dalam persamaan Ksp?</strong>
                  <p>Karena konsentrasi zat padat murni dianggap konstan dan tidak dimasukkan dalam ekspresi konstanta kesetimbangan kimia.</p>
                </div>
                <div className="mb-3">
                  <strong>8. Apa satuan pengukuran untuk Ksp?</strong>
                  <p>Konstanta kesetimbangan dalam kimia dianggap tidak bersatuan (tanpa dimensi) secara konvensional, meskipun konsentrasi ion yang digunakan dalam molaritas.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KspCalculator;